import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Col, Form, Row } from "react-bootstrap";
import { NorthOutlined, SouthOutlined } from "@mui/icons-material";
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import SimpleSelect from '../common/SimpleSelect';
import MultiSelect from '../common/MultiSelect';
import ToolTip from '../ToolTip.jsx';
import DefaultInput from "../common/DefaultInput"
import { useSelector } from 'react-redux';
import MetafieldsPopup from './MetafieldsPopup.jsx';
import SalesChannel from './SalesChannel.jsx';
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssetsFamiliesPopup from './assetFamilies.jsx';

function ProductMapping({
  handleChange,
  handleBlur,
  values,
  touched,
  errors,
  handleDefault,
  setFieldValue,
  setFieldTouched,
  productValues,
  mappingName,
  showToast
}) {
  const productSavedValues = useSelector(state => state.config.data);
  const relationship = useSelector(state => state.associationOptions.options);
  const optionsData = useSelector(state => state.PImAttributes.optionsData);
  const relatedProductsoptionsData = useSelector(state => state.associationOptions.options)

  const [isDefault, setIsDefault] = useState({});
  const [isClick, setIsClick] = useState({});
  const [showFields, setShowFields] = useState(false);
  const [defaultValues, setDefaultValues] = useState({});
  const [metafieldToggle, setMetafieldToggle] = useState(false);
  const [salesChannelsToggle, setsalesChannelsToggle] = useState(false);
  const [assetsFamiliesToggle,setAssetsFamiliesToggle] = useState(false);

  const fieldsToShow = useMemo(() =>
    showFields ? productValues : productValues.slice(0, 5),
    [showFields, productValues]
  );

  const titleMap = useMemo(() => ({
    productOrganization: "Product Organization",
    searchEngineListing: "Search Engine Listing",
    importImages: "Additional Mapping",
    variantMapping: "Variants",
    publishingSettings: "Publishing Settings",
    relatedproduct: "Product Relationship Mapping",
    default: mappingName.charAt(0).toUpperCase() + mappingName.slice(1)
  }), [mappingName]);

  // Memoized options
  const options = useMemo(() => [
    { value: 'true', label: 'Yes' },
    { value: 'false', label: 'No' }
  ], []);

  // Check if attribute mapping is valid
  const isAttributeMappingValid = useMemo(() =>
    productSavedValues?.attributeMapping !== null &&
    (Object.keys(productSavedValues?.attributeMapping || {}).length > 0),
    [productSavedValues]
  );

  const salesChannel = (field) => {
    return (
      <>
        <a className='fs-14' onClick={() => setsalesChannelsToggle(!salesChannelsToggle)} style={{ width: "auto", cursor: "pointer", color: "black", fontWeight: "600px" }}>
          <InfoOutlinedIcon
            sx={{ color: "gray", fontSize: 18, cursor: "pointer", marginRight: "4px", paddingBottom: "2px" }}
            aria-label="Information"
          />
          Help for configuration: Available Sales Channels
        </a>
        {salesChannelsToggle && <SalesChannel showToast={showToast} />}
      </>
    );
  }

  // Initialize default values and click states
  useEffect(() => {
    const defaultValuesIdentifier = {};
    const newDefaultValues = {};
    const clickObj = {};

    productValues.forEach((element) => {
      defaultValuesIdentifier[element.code] = false;

      if (productSavedValues !== null && isAttributeMappingValid) {
        let productMapping = productSavedValues?.attributeMapping?.[mappingName]?.[element.code];

        if (!productMapping) {
          productMapping = productSavedValues?.attributeMapping?.[element.code];
        }

        if (!productMapping && mappingName === "variantMapping") {
          const cleanCode = element.code.replace("variant_", "");
          productMapping = productSavedValues?.attributeMapping?.[mappingName]?.[cleanCode];
        }

        if (productMapping) {
          const def = productMapping.clientDefaultValue?.trim() || productMapping.default?.trim();

          if (def) {
            newDefaultValues[element.code] = true;
            clickObj[element.code] = false;
          } else if (productMapping.pimAttribute || (Array.isArray(productMapping) && productMapping.length > 0)) {
            newDefaultValues[element.code] = false;
            clickObj[element.code] = true;
          }
        }
      }
    });

    setIsDefault(prev => ({ ...prev, ...newDefaultValues }));
    setIsClick(prev => ({ ...prev, ...clickObj }));
    setDefaultValues(newDefaultValues);
  }, [productValues, productSavedValues, isAttributeMappingValid, mappingName]);

  // Handle default values
  useEffect(() => {
    handleDefault(isDefault, productValues);
  }, [isDefault, handleDefault, productValues]);

  // Memoized sort function
  const sortArray = useCallback((array) => {
    if (array.length > 0) {
      return [...array].sort((a, b) => a.label.localeCompare(b.label));
    }
    return array;
  }, []);

  // Memoized field options calculation
  const getFieldOptions = useCallback((field) => {
    const set = new Set();
    const arr = [];

    field.eligibleDataType.forEach((dataType) => {
      optionsData.forEach((optionGroup) => {
        if (optionGroup[dataType]) {
          optionGroup[dataType].forEach((option) => {
            if (!set.has(option.code)) {
              set.add(option.code);
              arr.push({
                value: option.code,
                label: option.displayName ?? option.code,
                pimType: dataType
              });
            }
          });
        }
      });
    });

    return sortArray(arr);
  }, [optionsData, sortArray]);

  // Memoized relationship options finder
  const findRelationShipOptions = useCallback((fieldCode, arr) => {
    if (fieldCode === "associationType") {
      return relationship;
    } else if (fieldCode === "importimages") {
      return options;
    }
    return arr;
  }, [relationship, options]);

  // Toggle handlers
  const toggleIsDefault = useCallback((field, value) => {
    setIsDefault(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleTestDefault = useCallback((field, value) => {
    setIsClick(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleShowFields = useCallback(() => {
    setShowFields(prev => !prev);
  }, []);

  // Clear field value handler
  const handleClearField = useCallback((fieldCode) => {
    handleChange({ target: { name: fieldCode, value: "" } });
    toggleIsDefault(fieldCode, false);
    handleTestDefault(fieldCode, true);
  }, [handleChange, toggleIsDefault, handleTestDefault]);

  // Render field actions based on conditions
  const renderFieldActions = useCallback((field) => {
    const fieldCode = field.code;
    const fieldValue = values[fieldCode];
    const isSimpleField = field.field_type === "simple";

    if (isSimpleField && isClick[fieldCode] && fieldValue === "" && defaultValues[fieldCode]) {
      return (
        <Col sm={1} className="ps-0 pt-1 d-flex align-items-baseline justify-content-center productAction w-auto">
          <button
            type="button"
            onClick={() => { toggleIsDefault(fieldCode, true); handleTestDefault(fieldCode, false); }}
            className="channelActionBtn fs14 mt-2 text-decoration-underline"
          >
            Add default value
          </button>
        </Col>
      );
    }

    if (isSimpleField && (isDefault[fieldCode] || defaultValues[fieldCode])) {
      return (
        <Col sm={1} className="ps-0 pt-1 ms-1 productAction">
          <button
            type="button"
            onClick={() => handleClearField(fieldCode)}
            className="channelActionBtn"
          >
            <DeleteOutlineIcon style={{ fontSize: "30px" }} />
          </button>
        </Col>
      );
    }

    if (isSimpleField && !isDefault[fieldCode] && fieldValue && fieldValue !== "") {
      return (
        <Col sm={1} className="ps-0 pt-1 ms-1 productAction">
          <button
            type="button"
            onClick={() => handleClearField(fieldCode)}
            className="channelActionBtn"
          >
            <DeleteOutlineIcon style={{ fontSize: "30px" }} />
          </button>
        </Col>
      );
    }

    if (isSimpleField && !["required", "optional"].includes(field.componentType) || isDefault[fieldCode]) {
      return (
        <Col sm={1} className="ps-0 pt-1 d-flex align-items-baseline justify-content-center productAction w-auto">
          <button
            type="button"
            onClick={() => { toggleIsDefault(fieldCode, true); handleTestDefault(fieldCode, false); }}
            className="channelActionBtn fs14 mt-2 text-decoration-underline"
          >
            Add default value
          </button>
        </Col>
      );
    }

    return null;
  }, [isClick, isDefault, values, toggleIsDefault, handleTestDefault, handleClearField, defaultValues]);

  return (
    <>
    
    <div className="mapping_component bg-light p-4 rounded mb-10">
      <Row className="border-bottom pb-3 mb-3 align-items-center">
        <Col sm={12}>
           {/* need to place the HTML in below h6 tag "{mappingName === "metafields" && <button type="button" className="modal_button" onClick={() => { setMetafieldToggle(true) }}>Metafields Mapping</button>}" */}
          <h6 className="fw-bold m-0">{titleMap[mappingName] || titleMap.default} </h6>
        </Col>
      </Row>
      {/* {metafieldToggle && <MetafieldsPopup toggle={metafieldToggle} setToggle={setMetafieldToggle} />} */}
      {assetsFamiliesToggle && <AssetsFamiliesPopup toggle={assetsFamiliesToggle} setToggle={setAssetsFamiliesToggle} showToast={showToast}/>}
      {mappingName === "productOrganization" && (
        <Row className="mappingInput">
          <Col sm={5}>
            <p className="mb-4"><strong>Shopify Field</strong></p>
          </Col>
          <Col sm={5} className="mx-5 pimAttribute">
            <p className="mb-4"><strong>PIM Attribute</strong></p>
          </Col>
        </Row>
      )}

      {fieldsToShow.map((field) => {
        const fieldOptions = getFieldOptions(field);
        const relationshipOptions = findRelationShipOptions(field.code, fieldOptions);

        return (
          <Row className="mappingInput mb-15" key={field.code}>
            <Col sm={5}>
              <Form.Group className="mb-3 d-flex align-items-center position-relative">
                <Form.Control
                  type="text"
                  name={field.code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={field.title}
                  readOnly
                />
                <ToolTip text={field.Tooltip_Message} />
              </Form.Group>
               {field.code === "productImages" && <button type='button' className='modal_button modal_Asset_selection_button' onClick={() => setAssetsFamiliesToggle(true)}>Assets Mapping</button>}
            </Col>

            <Col sm={1} className="d-flex justify-content-center compareIcon">
              <KeyboardBackspaceIcon className="mt-2" />
            </Col>

            <Col sm={5}>
              <Form.Group className="mb-3" controlId={field.code}>
                {field.field_type === "simple" ? (
                  field.code === "importImages" ? (
                    <SimpleSelect
                      name={field.code}
                      value={values[field.code]}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      touched={touched[field]}
                      options={options}
                      isInvalid={touched[field.code] && errors[field.code]}
                      error={errors[field.code]}
                    />
                  ) : field.code === "relatedproduct" ? (
                    <SimpleSelect
                      name={field.code}
                      value={values[field.code]}
                      onChange={setFieldValue}
                      onBlur={setFieldTouched}
                      touched={touched[field]}
                      options={relatedProductsoptionsData}
                      isInvalid={touched[field.code] && errors[field.code]}
                      error={errors[field.code]}
                    />
                  ) : (
                    // Check if form value exists and is an object (Select Mode)
                    isClick[field.code] || (values[field.code] && typeof values[field.code] === 'object') ? (
                      <SimpleSelect
                        name={field.code}
                        value={values[field.code]}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        touched={touched[field]}
                        options={relationshipOptions}
                        isInvalid={touched[field.code] && errors[field.code]}
                        error={errors[field.code]}
                      />
                    ) : isDefault[field.code] ? (
                      <DefaultInput
                        name={field.code}
                        value={values[field.code]}
                        handleChange={handleChange}
                        handleBlur={handleBlur}
                        placeholder="Enter default value"
                        isInvalid={touched[field.code] && errors[field.code]}
                        error={errors[field.code]}
                      />
                    ) : (
                      <SimpleSelect
                        name={field.code}
                        value={values[field.code]}
                        onChange={setFieldValue}
                        onBlur={setFieldTouched}
                        touched={touched[field]}
                        options={relationshipOptions}
                        isInvalid={touched[field.code] && errors[field.code]}
                        error={errors[field.code]}
                      />
                    )
                  )
                ) : (
                  <MultiSelect
                    name={field.code}
                    value={values[field.code]}
                    onChange={setFieldValue}
                    onBlur={setFieldTouched}
                    touched={touched[field]}
                    options={relationshipOptions}
                    isInvalid={touched[field.code] && errors[field.code]}
                    error={errors[field.code]}
                  />
                )}

                <Form.Control.Feedback type="invalid">
                  {errors[field.code]}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            {renderFieldActions(field)}
            {field.code === "salesChannels" && salesChannel(field)}

          </Row>
        );
      })}

      {productValues.length > 5 && (
        <Row>
          <Col sm={10} className="d-flex justify-content-center mt-3">
            <button
              type="button"
              className="border-0 show-more ms-8"
              onClick={toggleShowFields}
            >
              {showFields ? (
                <NorthOutlined style={{ fontSize: "14px" }} />
              ) : (
                <SouthOutlined style={{ fontSize: "14px" }} />
              )}{" "}
              <span>{showFields ? "Less" : "More"}</span>
            </button>
          </Col>
        </Row>
      )}
    </div>
    </>
  );
}

export default React.memo(ProductMapping);